import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/App";
import { SiteHeader, SiteFooter, useMeta, useSite } from "@/components/site/Chrome";


function Pill({ children, tone = "muted", testid }) {
  const tones = { muted: { border: "var(--hair)", color: "var(--ink-5)" }, live: { border: "var(--ink)", color: "var(--ink)" }, hit: { border: "var(--ac)", color: "var(--ac-deep)", background: "var(--ac-tint)" } };
  return <span data-testid={testid} className="er-mono" style={{ fontSize: 10.5, letterSpacing: "0.1em", padding: "3px 9px", border: "1px solid", whiteSpace: "nowrap", ...tones[tone] }}>{children}</span>;
}

function ExamRow({ ex, duration, onStart, navigate }) {
  const GUARANTEE_PCT = useSite()?.guarantee?.pass_pct ?? 85;
  const hit = (ex.best_pct || 0) >= GUARANTEE_PCT;
  return (
    <div className="er-row er-dash-row" data-testid={`exam-card-${ex.exam_number}`}>
      <span className="er-mono" style={{ fontSize: 12, color: "var(--ink-5)", paddingTop: 3 }}>{String(ex.exam_number).padStart(2, "0")}</span>
      <div style={{ minWidth: 0 }}>
        <p style={{ fontWeight: 600, fontSize: 15.5, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>Exam {ex.exam_number}
          {ex.in_progress_id ? <Pill tone="live" testid={`exam-status-${ex.exam_number}`}>IN PROGRESS</Pill>
            : ex.best_pct != null ? <Pill tone={hit ? "hit" : "muted"} testid={`exam-status-${ex.exam_number}`}>{hit ? "COUNTS TOWARD GUARANTEE" : `BELOW ${GUARANTEE_PCT}%`}</Pill>
            : <Pill testid={`exam-status-${ex.exam_number}`}>NOT STARTED</Pill>}
        </p>
        <p style={{ marginTop: 4, fontSize: 13.5, color: "var(--ink-4)" }}>{ex.question_count} questions · {duration} minutes · {ex.attempt_count} attempt{ex.attempt_count === 1 ? "" : "s"}</p>
      </div>
      <p className="er-mono" style={{ fontSize: 20, textAlign: "right", color: ex.best_pct == null ? "var(--ink-6)" : hit ? "var(--ac-deep)" : "var(--ink)" }} data-testid={`exam-best-${ex.exam_number}`}>{ex.best_pct != null ? `${Math.round(ex.best_pct)}%` : "—"}</p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
        {ex.in_progress_id ? (
          <button data-testid={`exam-resume-${ex.exam_number}`} onClick={() => navigate(`/exam/${ex.in_progress_id}`)} className="er-btn-dark" style={{ padding: "10px 18px", fontSize: 14 }}>Resume</button>
        ) : (
          <>
            <button data-testid={`exam-start-testing-${ex.exam_number}`} onClick={() => onStart(ex.exam_number, "testing")} className="er-btn" style={{ padding: "10px 18px", fontSize: 14 }}>{ex.attempt_count ? "Retake" : "Start"}</button>
            <button data-testid={`exam-start-study-${ex.exam_number}`} onClick={() => onStart(ex.exam_number, "study")} className="er-btn-secondary" style={{ padding: "10px 16px", fontSize: 14 }}>Study</button>
          </>
        )}
        {ex.last_report_id && <Link data-testid={`exam-report-${ex.exam_number}`} to={`/report/${ex.last_report_id}`} className="er-btn-secondary" style={{ padding: "10px 16px", fontSize: 14 }}>Report</Link>}
      </div>
    </div>
  );
}

function CertBlock({ cert, accessEnd }) {
  const GUARANTEE_PCT = useSite()?.guarantee?.pass_pct ?? 85;
  const [exams, setExams] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    api.get(`/certs/${cert.slug}/exams`).then((r) => setExams(r.data)).catch(() => setExams({ exams: [] }));
  }, [cert.slug]);

  const start = async (n, mode) => {
    try {
      const { data } = await api.post("/exam-sessions", { cert_slug: cert.slug, exam_number: n, mode });
      navigate(`/exam/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const rows = exams?.exams || [];
  const hits = rows.filter((e) => (e.best_pct || 0) >= GUARANTEE_PCT).length;
  const access = accessEnd === "admin" ? "ADMIN ACCESS" : `ACCESS UNTIL ${String(accessEnd).slice(0, 10)}`;
  return (
    <section className="er-dash-card" data-testid={`dash-cert-${cert.slug}`}>
      <div className="er-rule" />
      <div className="er-dash-head">
        <div>
          <p className="er-kicker">{cert.vendor.toUpperCase()}{cert.exam_code ? ` · ${cert.exam_code}` : ""} · {access}</p>
          <h2 className="er-display" style={{ marginTop: 10, fontWeight: 700, fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.1 }}>{cert.name}</h2>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Link data-testid="dash-cert-page-link" to={`/${cert.slug}`} className="er-btn-secondary" style={{ padding: "10px 16px", fontSize: 14 }}>Exam details</Link>
          <Link data-testid="dash-guarantee-link" to="/guarantee" className="er-btn-secondary" style={{ padding: "10px 16px", fontSize: 14 }}>Guarantee terms</Link>
        </div>
      </div>
      {rows.length > 0 && (
        <div style={{ padding: "0 26px" }} data-testid={`dash-guarantee-${cert.slug}`}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <p className="er-label">GUARANTEE PROGRESS</p>
            <p style={{ fontSize: 13.5, color: "var(--ink-4)" }} data-testid={`dash-guarantee-count-${cert.slug}`}>{hits} of {rows.length} exams at {GUARANTEE_PCT}% or higher{hits === rows.length ? " — you qualify" : ""}</p>
          </div>
          <div className="er-guar-grid" style={{ marginTop: 12 }}>
            {rows.map((e) => (
              <div key={e.exam_number} className={`er-guar-cell${(e.best_pct || 0) >= GUARANTEE_PCT ? " hit" : ""}`}>
                <p className="er-label" style={{ fontSize: 10, letterSpacing: "0.08em" }}>EXAM {e.exam_number}</p>
                <p className="er-mono" style={{ marginTop: 6, fontSize: 16 }}>{e.best_pct != null ? `${Math.round(e.best_pct)}%` : "—"}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={{ padding: "26px 26px 8px" }}>
        <div className="er-rows">
          {rows.map((ex) => <ExamRow key={ex.exam_number} ex={ex} duration={exams.duration_min} onStart={start} navigate={navigate} />)}
        </div>
        {exams && rows.length === 0 && <p style={{ fontSize: 14, color: "var(--ink-5)", paddingBottom: 18 }}>No exams available.</p>}
        {!exams && <p style={{ fontSize: 14, color: "var(--ink-5)", paddingBottom: 18 }}>Loading exams…</p>}
      </div>
    </section>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [certs, setCerts] = useState([]);
  useMeta("My exams — ExamRay");
  useEffect(() => { api.get("/certs").then((r) => setCerts(r.data.certs)); }, []);
  if (user === null) return <div className="er"><SiteHeader /><p style={{ padding: 32, color: "var(--ink-5)" }}>Loading…</p></div>;
  if (user === false) return (
    <div className="er"><SiteHeader />
      <div className="er-page" style={{ maxWidth: 720 }} data-testid="dash-signed-out">
        <p className="er-kicker">MY EXAMS</p>
        <h1 className="er-h1">Sign in to see your exams.</h1>
        <p style={{ marginTop: 18, fontSize: 16, color: "var(--ink-3)" }}>We send a one-time code to your email. No password.</p>
        <Link to="/login" className="er-btn" style={{ marginTop: 26 }} data-testid="dash-signin-link">Sign in</Link>
      </div>
      <SiteFooter certs={certs} /></div>
  );

  const activeEnts = (user.entitlements || []).filter((e) => e.status === "active");
  const blocks = user.is_admin
    ? certs.map((c) => ({ cert: c, accessEnd: "admin" }))
    : activeEnts.map((e) => ({ cert: certs.find((c) => c.id === e.cert_id), accessEnd: e.access_end })).filter((b) => b.cert);

  return (
    <div className="er" data-testid="dashboard-page">
      <SiteHeader />
      <div className="er-page er-fade">
        <p className="er-kicker" data-testid="dash-email">MY EXAMS · {user.email.toUpperCase()}</p>
        <h1 className="er-h1" style={{ fontSize: 40 }}>Your exams.</h1>
        <p style={{ marginTop: 14, fontSize: 16, lineHeight: 1.6, color: "var(--ink-3)", maxWidth: "38em" }}>Testing mode is timed and scored like the real thing. Study mode shows the explanation after every question. Best score per exam counts toward the guarantee.</p>
        <div style={{ marginTop: 36, display: "flex", flexDirection: "column", gap: 28 }}>
          {blocks.length === 0 ? (
            <div style={{ border: "1px dashed var(--field)", padding: "44px 32px", textAlign: "center" }} data-testid="no-access">
              <p style={{ fontSize: 16, color: "var(--ink-3)" }}>No active access yet.</p>
              <Link to="/#catalog" className="er-btn" style={{ marginTop: 18 }} data-testid="dash-browse-link">Browse certifications</Link>
            </div>
          ) : (
            blocks.map((b) => <CertBlock key={b.cert.slug} cert={b.cert} accessEnd={b.accessEnd} />)
          )}
        </div>
      </div>
      <SiteFooter certs={certs} />
    </div>
  );
}
